import { DateTime } from 'luxon';
import React from 'react';
import { useRouteMatch } from 'react-router';
import useSessionStorage from 'react-use/lib/useSessionStorage';
import {
  Order,
  PaymentPayload,
  Person,
  ProductMetaData,
  Quiz,
} from '../swagger';
import { api } from '../utils/api';
import { Account } from './useAccount';
import { useBrokers } from './useBrokers';
import { usePricings } from './usePricings';
import { useQuiz } from './useQuiz';

const initialDate = DateTime.utc().plus({ day: 1 }).toISODate();

interface useCheckupProps {
  sessionStorageKey: string;
  order?: Order;
  defaultPaymentPeriod?: number;
  skipQuiz?: boolean;
}

export const useProductOrder = ({
  sessionStorageKey,
  order,
  defaultPaymentPeriod = 1,
  skipQuiz = false,
}: useCheckupProps) => {
  const { loading: accountLoading, brokers, admin } = Account.useContainer();
  const {
    params: { offerId: newOfferId },
  } = useRouteMatch<{ offerId?: string }>();
  const { saveOrCreateQuiz } = useQuiz();

  const [ready, setReady] = React.useState(false);
  const [selectedPricing, setSelectedPricing] = React.useState<string>();
  const [selectedBroker, setSelectedBroker] = React.useState<string>();
  const [meta, setMeta] = React.useState<ProductMetaData>({});

  const [persons, setPersons] = useSessionStorage<Person[]>(
    `${sessionStorageKey}/persons`,
    [],
  );
  const [pendingMethod, setPendingMethod] = useSessionStorage<
    PaymentPayload | undefined
  >(`${sessionStorageKey}/pendingMethod`, undefined);
  const [paymentPeriod, setPaymentPeriod] = useSessionStorage<number>(
    `${sessionStorageKey}/paymentPeriod`,
    order?.paymentPeriod || defaultPaymentPeriod,
  );
  const [startDate, setStartDate] = useSessionStorage<string>(
    `${sessionStorageKey}/startDate`,
    initialDate,
  );
  const [consent, setConsent] = useSessionStorage<boolean>(
    `${sessionStorageKey}/consent`,
    false,
  );
  const [offerId, setOfferId] = useSessionStorage<string | undefined>(
    `${sessionStorageKey}/offerId`,
  );

  const isAdminOrBroker = !!(admin || brokers.length);
  const isBroker = isAdminOrBroker && !offerId;

  // select first available pricing
  const [pricings] = usePricings(isBroker);
  const firstPricing = pricings?.[0]?.id;
  React.useEffect(() => {
    if (!offerId && firstPricing && !selectedPricing) {
      setSelectedPricing(firstPricing);
    }
  }, [offerId, firstPricing, selectedPricing]);

  // select first available broker
  const availableBrokers = useBrokers(isBroker);
  const firstBroker = !!brokers.length && availableBrokers?.[0]?.id;
  React.useEffect(() => {
    if (!offerId && firstBroker && !selectedBroker) {
      setSelectedBroker(firstBroker);
    }
  }, [offerId, firstBroker, selectedBroker]);

  React.useEffect(() => {
    if (
      newOfferId &&
      newOfferId !== offerId &&
      !accountLoading &&
      !isAdminOrBroker
    ) {
      setOfferId(newOfferId);
    }
    // accountLoading, not a second isAdminOrBroker. The guard above waits for
    // the account to load, so without this the effect never re-ran once it had
    // and an offer arriving during load was dropped.
  }, [newOfferId, offerId, isAdminOrBroker, accountLoading, setOfferId]);

  React.useEffect(() => {
    if (offerId && offerId.length < 36) {
      setOfferId(undefined);
    }
  }, [offerId, setOfferId]);

  React.useEffect(() => {
    if (accountLoading || ready) {
      return;
    }

    (async () => {
      const offerIdToFetch = newOfferId || offerId;

      if (offerIdToFetch) {
        const offer = await api.orderControllerGetOffer(offerIdToFetch);

        setPersons(offer.persons);
        setPaymentPeriod(offer.paymentPeriod ?? defaultPaymentPeriod);
        setConsent(offer.consent || false);
        setStartDate(offer.startDate || initialDate);
        setPendingMethod(offer.paymentMethod);
        setSelectedPricing(offer.pricingId);
        setReady(true);
        return;
      }

      if (!isBroker && persons.length) {
        setReady(true);
        return;
      }

      setPersons([
        {
          type: Person.TypeEnum.Main,
          quiz: skipQuiz ? undefined : await saveOrCreateQuiz({} as Quiz),
        },
      ]);
      setPaymentPeriod(defaultPaymentPeriod);
      setConsent(false);
      setStartDate(initialDate);
      setPendingMethod(undefined);
      setSelectedPricing(undefined);
      setSelectedBroker(undefined);
      setReady(true);
      setOfferId(undefined);
    })();
    // Seeds the order once, guarded by `ready`. The setters and
    // saveOrCreateQuiz are all rebuilt each render, and every one of them is
    // written by this effect -- depending on them would restart the seeding.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [persons.length, ready, accountLoading, isBroker, offerId, newOfferId]);

  return {
    selectedPricing,
    selectedBroker,
    pendingMethod,
    paymentPeriod,
    startDate,
    consent,
    offerId,
    persons,
    ready,
    isBroker,
    pricings,
    availableBrokers,
    meta,
    // methods
    setSelectedPricing,
    setSelectedBroker,
    setPersons,
    setPendingMethod,
    setConsent,
    setPaymentPeriod,
    setStartDate,
    setReady,
    setMeta,
  };
};
